import * as React from 'react'
import { cn } from '../../lib/utils'
import { EntityGlyph, type EntityType } from './entity-glyph'
import { Link } from './link'
import { type BadgeColor } from './badge'

/**
 * EntitySummaryCard — a linked record shown as a compact card: leading
 * {@link EntityGlyph} (or a `media` override for a logo/avatar), the record name
 * as a link, a type/role subtitle, an optional trailing badge or actions, and a
 * few metadata rows below. The on-page counterpart to {@link EntityPreviewCard}
 * (which is the popover preview) — used to surface a *related* entity inside a
 * detail-page section: the primary company on a deal, a contact's company.
 *
 *   <EntitySummaryCard
 *     type="company" name="Grand Peaks Foods" href="/companies/42"
 *     subtitle="Regional Chain" badge={<Badge color="green">Active</Badge>}>
 *     <EntitySummaryRow icon={<MapPin />}>Denver, CO · 14 locations</EntitySummaryRow>
 *   </EntitySummaryCard>
 *
 * `href` makes the name a quiet link (brand on hover); omit it for a plain
 * name. `bordered` `false` drops the card chrome so it can nest inside another
 * container (a section that already has its own border).
 */

export interface EntitySummaryCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Record type — resolves the leading glyph + tint. */
  type?: EntityType
  /** Glyph icon override. */
  icon?: React.ReactNode
  /** Glyph tint override. */
  color?: BadgeColor
  /** Leading-slot override — a company logo / avatar in place of the glyph. */
  media?: React.ReactNode
  /** Record name — the card title. */
  name: React.ReactNode
  /** Link target — renders the name as a quiet link. */
  href?: string
  /** Secondary line under the name — a type, a role · company. */
  subtitle?: React.ReactNode
  /** Trailing badge beside the name (a status pill). */
  badge?: React.ReactNode
  /** Trailing actions (an edit pencil, a menu) — top-right. */
  actions?: React.ReactNode
  /** Metadata rows — compose {@link EntitySummaryRow}s. */
  children?: React.ReactNode
  /** Card border + surface. Default `true`; `false` nests bare in a container. */
  bordered?: boolean
}

export const EntitySummaryCard = React.forwardRef<
  HTMLDivElement,
  EntitySummaryCardProps
>(
  (
    {
      type,
      icon,
      color,
      media,
      name,
      href,
      subtitle,
      badge,
      actions,
      children,
      bordered = true,
      className,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        bordered &&
          'rounded-lg border border-fbui-gray-200 bg-fbui-base-white p-3',
        className
      )}
      {...props}
    >
      {/* Center the glyph against a single-line header (no subtitle) so the name
          doesn't sit top-aligned with an empty gap below it; top-align when a
          subtitle makes the header two lines. */}
      <div
        className={cn(
          'flex gap-3',
          subtitle != null ? 'items-start' : 'items-center'
        )}
      >
        {media ?? <EntityGlyph type={type} icon={icon} color={color} size="sm" />}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            {href != null ? (
              <Link
                href={href}
                variant="quiet"
                className="truncate font-semibold text-fbui-gray-900"
              >
                {name}
              </Link>
            ) : (
              <span className="truncate text-sm font-semibold text-fbui-gray-900">
                {name}
              </span>
            )}
            {badge}
          </div>
          {subtitle != null && (
            <span className="truncate text-xs text-fbui-gray-500">{subtitle}</span>
          )}
        </div>
        {actions != null && <div className="-mr-1 -mt-0.5 shrink-0">{actions}</div>}
      </div>
      {children != null && (
        <div className="mt-2.5 flex flex-col gap-1.5 pl-11">{children}</div>
      )}
    </div>
  )
)
EntitySummaryCard.displayName = 'EntitySummaryCard'

export interface EntitySummaryRowProps {
  /** Leading muted icon. */
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/** One metadata row inside an {@link EntitySummaryCard}: a muted icon + text. */
export const EntitySummaryRow = ({
  icon,
  children,
  className,
}: EntitySummaryRowProps) => (
  <div
    className={cn(
      'flex items-center gap-2 text-xs text-fbui-gray-600',
      className
    )}
  >
    {icon != null && (
      <span className="shrink-0 text-fbui-gray-400 [&>svg]:size-4">{icon}</span>
    )}
    <span className="min-w-0 flex-1 truncate">{children}</span>
  </div>
)
EntitySummaryRow.displayName = 'EntitySummaryRow'
